import { DEBUG_MODE } from '../spdConfig';

export class DrawingAnalizer {
  protected static nonEmptyPixelsParam = 500;
  protected static linesParam = 3;
  protected static longestLineLengthForMinLinesParam = 1000;
  protected static longestLineLengthParam = 100;
  protected static boundingBoxWidthParam = 50;
  protected static boundingBoxHeightParam = 50;

  protected static countNonEmptyPixels(imageData: ImageData) {
    let nonEmptyPixelCount = 0;
    const pixels = imageData.data;

    for (let i = 0; i < pixels.length; i += 4) {
      const alpha = pixels[i + 3]; // Kanał alfa
      if (alpha > 0) {
        // Piksel jest nieprzezroczysty
        nonEmptyPixelCount++;
      }
    }
    return nonEmptyPixelCount;
  }

  protected static getBoundingBox(
    imageData: ImageData,
    width: number,
    height: number,
  ) {
    const pixels = imageData.data;

    let minX = width,
      minY = height,
      maxX = 0,
      maxY = 0;
    let hasContent = false;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = (y * width + x) * 4;
        if (pixels[index + 3] > 0) {
          // Sprawdzenie przezroczystości
          hasContent = true;
          if (x < minX) {
            minX = x;
          }
          if (x > maxX) {
            maxX = x;
          }
          if (y < minY) {
            minY = y;
          }
          if (y > maxY) {
            maxY = y;
          }
        }
      }
    }

    if (!hasContent) {
      return null;
    } // Brak treści

    return {
      minX,
      minY,
      maxX,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }
  protected static floodFill(
    data: Uint8ClampedArray,
    width: number,
    height: number,
    x: number,
    y: number,
    visited: Set<string>,
  ) {
    const stack = [[x, y]];
    const directions = [
      [0, 1],
      [1, 0],
      [0, -1],
      [-1, 0], // Prawo, dół, lewo, góra
    ];

    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) {
        continue; // W przypadku pustego stosu, przejdź do kolejnej iteracji
      }

      const [cx, cy] = current;
      const key = `${cx},${cy}`;
      if (visited.has(key) || cx < 0 || cy < 0 || cx >= width || cy >= height) {
        continue;
      }

      // Sprawdzenie, czy piksel jest częścią rysunku (kanał alfa > 0)
      const index = (cy * width + cx) * 4;
      if (data[index + 3] > 0) {
        visited.add(key);
        for (const [dx, dy] of directions) {
          stack.push([cx + dx, cy + dy]);
        }
      }
    }
  }

  protected static countDistinctLines(imageData: ImageData): number {
    const { data, width, height } = imageData;
    const visited = new Set<string>();
    let lineCount = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = (y * width + x) * 4;
        const alpha = data[index + 3]; // Kanał alfa

        if (alpha > 0 && !visited.has(`${x},${y}`)) {
          // Nowa linia
          lineCount++;
          this.floodFill(data, width, height, x, y, visited);
        }
      }
    }
    return lineCount;
  }

  protected static calculateLongestLineLength(imageData: ImageData): number {
    const { data, width, height } = imageData;
    const visited = new Set<string>();
    let maxLength = 0;

    // Funkcja pomocnicza do sprawdzania, czy piksel jest wypełniony
    function isPixelFilled(x: number, y: number): boolean {
      if (x < 0 || y < 0 || x >= width || y >= height) {
        return false;
      }
      const index = (y * width + x) * 4;
      return data[index + 3] > 0; // Kanał alfa > 0 oznacza piksel "wypełniony"
    }

    // Funkcja DFS do zliczania długości spójnej linii
    function dfs(x: number, y: number): number {
      const stack = [[x, y]];
      let length = 0;

      while (stack.length > 0) {
        const current = stack.pop();
        if (!current) {
          continue; // Wartość `undefined`, pomijamy iterację
        }

        const [cx, cy] = current;
        const key = `${cx},${cy}`;
        if (visited.has(key)) {
          continue;
        }
        visited.add(key);
        length++;

        // Sąsiedzi: 4-kierunkowi i diagonalni
        const neighbors = [
          [0, 1],
          [1, 0],
          [0, -1],
          [-1, 0],
          [1, 1],
          [1, -1],
          [-1, 1],
          [-1, -1],
        ];

        for (const [dx, dy] of neighbors) {
          const nx = cx + dx;
          const ny = cy + dy;
          if (!visited.has(`${nx},${ny}`) && isPixelFilled(nx, ny)) {
            stack.push([nx, ny]);
          }
        }
      }

      return length;
    }

    // Przeszukiwanie pikseli
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const key = `${x},${y}`;
        if (!visited.has(key) && isPixelFilled(x, y)) {
          // Uruchom DFS i znajdź długość spójnej linii
          const lineLength = dfs(x, y);
          maxLength = Math.max(maxLength, lineLength);
        }
      }
    }

    return maxLength;
  }

  // eslint-disable-next-line complexity
  public static isNotSufficientlyComplex(
    canvas: HTMLCanvasElement,
    checkComplex = false,
    param: number[] = [],
  ) {
    const blankCanvas = document.createElement('canvas');
    blankCanvas.width = canvas.width;
    blankCanvas.height = canvas.height;

    const empty = canvas.toDataURL() === blankCanvas.toDataURL();

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const nonEmptyPixels = this.countNonEmptyPixels(imageData);
      const boundingBox = this.getBoundingBox(
        imageData,
        canvas.width,
        canvas.height,
      );
      const lines = this.countDistinctLines(imageData);
      const longestLineLength = this.calculateLongestLineLength(imageData);
      DEBUG_MODE &&
        console.log(
          empty,
          nonEmptyPixels,
          lines,
          longestLineLength,
          boundingBox?.width,
          boundingBox?.height,
        );

      if (empty) {
        return 0;
      }

      if (checkComplex) {
        const nonEmptyPixelsParam = param[0] || this.nonEmptyPixelsParam;
        const linesParam = param[1] || this.linesParam;
        const longestLineLengthForMinLinesParam =
          param[2] || this.longestLineLengthForMinLinesParam;
        const longestLineLengthParam = param[3] || this.longestLineLengthParam;
        const boundingBoxWidthParam = param[4] || this.boundingBoxWidthParam;
        const boundingBoxHeightParam = param[5] || this.boundingBoxHeightParam;
        if (
          nonEmptyPixels < nonEmptyPixelsParam ||
          (lines < linesParam &&
            longestLineLength < longestLineLengthForMinLinesParam) ||
          longestLineLength < longestLineLengthParam
        ) {
          return 1;
        }
        if (
          boundingBox &&
          (boundingBox.width < boundingBoxWidthParam ||
            boundingBox.height < boundingBoxHeightParam)
        ) {
          return 2;
        }
      }
      return -1; /// nie ma, żadnych uwag
    }

    return 0;
  }
}
